import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, Upload, FileText, CheckCircle, AlertTriangle,
  Package, User, Camera, X,
} from 'lucide-react';
import { api } from '@/utils/api';

interface WarrantyClaim {
  failureType: string;
  failureDescription: string;
  sitePhotos: string[];
  technicianReport: string;
  partsReplaced: { partName: string; serialNumber: string }[];
}

const FAILURE_TYPES = [
  'Manufacturing Defect',
  'Material Failure',
  'Component Failure',
  'Welding Failure',
  'PUF Insulation Failure',
  'Leakage',
  'Corrosion',
  'Performance Issue',
  'Other',
];

export function WarrantyClaimPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);

  const [claim, setClaim] = useState<WarrantyClaim>({
    failureType: '',
    failureDescription: '',
    sitePhotos: [],
    technicianReport: '',
    partsReplaced: [],
  });

  const [newPart, setNewPart] = useState({ partName: '', serialNumber: '' });

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/files', formData);
      const url = res.data.url || res.data.data?.url;
      if (url) setClaim((c) => ({ ...c, sitePhotos: [...c.sitePhotos, url] }));
    } catch {
      const reader = new FileReader();
      reader.onload = () => {
        setClaim((c) => ({ ...c, sitePhotos: [...c.sitePhotos, reader.result as string] }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addPart = () => {
    if (!newPart.partName) return;
    setClaim((c) => ({ ...c, partsReplaced: [...c.partsReplaced, newPart] }));
    setNewPart({ partName: '', serialNumber: '' });
  };

  const removePart = (idx: number) => {
    setClaim((c) => ({ ...c, partsReplaced: c.partsReplaced.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!claim.failureType || !claim.failureDescription) {
      alert('Please fill all required fields');
      return;
    }
    if (claim.sitePhotos.length === 0) {
      alert('Please upload at least one site photo');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/service-sop/complaints/${id}/warranty-claim`, claim);
      setClaimSubmitted(true);
      setTimeout(() => navigate(`/service-tickets/${id}`), 2000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to submit warranty claim');
    } finally {
      setSubmitting(false);
    }
  };

  if (claimSubmitted) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <div className="rounded-full bg-green-100 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-[var(--text-primary)]">Warranty Claim Submitted</h2>
        <p className="text-sm text-[var(--text-muted)] mt-2">Awaiting approval from Service Head</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Warranty Claim</h1>
        <p className="text-sm text-[var(--text-muted)]">Submit warranty claim for defective component</p>
      </div>

      <div className="space-y-6">
        {/* Failure Type */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4">Failure Type</h3>
          <div className="grid grid-cols-2 gap-3">
            {FAILURE_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setClaim((c) => ({ ...c, failureType: type }))}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  claim.failureType === type
                    ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]'
                    : 'border-[var(--border-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Failure Description */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4">Failure Description</h3>
          <textarea
            value={claim.failureDescription}
            onChange={(e) => setClaim((c) => ({ ...c, failureDescription: e.target.value }))}
            rows={4}
            placeholder="Describe the failure in detail..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
          />
        </div>

        {/* Site Photos */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Camera size={18} className="text-[var(--ink-0)]" />
            Site Photos <span className="text-red-500">*</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {claim.sitePhotos.map((url, idx) => (
              <div key={idx} className="relative">
                <img src={url} alt="" className="h-24 w-24 rounded-lg object-cover border" />
                <button
                  type="button"
                  onClick={() => setClaim((c) => ({ ...c, sitePhotos: c.sitePhotos.filter((_, i) => i !== idx) }))}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-[var(--border-primary)] hover:bg-[var(--bg-muted)]">
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              <Upload size={20} className="text-[var(--text-muted)]" />
            </label>
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-2">Upload clear photos of the failed component and installation site</p>
        </div>

        {/* Technician Report */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <FileText size={18} className="text-[var(--ink-0)]" />
            Technician Report
          </h3>
          <textarea
            value={claim.technicianReport}
            onChange={(e) => setClaim((c) => ({ ...c, technicianReport: e.target.value }))}
            rows={4}
            placeholder="Detailed report of findings and observations..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--ink-0)] focus:outline-none"
          />
        </div>

        {/* Parts Replaced */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Package size={18} className="text-[var(--ink-0)]" />
            Parts Replaced
          </h3>
          <div className="space-y-3 mb-4">
            {claim.partsReplaced.map((part, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-muted)]">
                <div>
                  <span className="font-medium text-[var(--text-primary)]">{part.partName}</span>
                  {part.serialNumber && <span className="text-xs text-[var(--text-muted)] ml-2">SN: {part.serialNumber}</span>}
                </div>
                <button onClick={() => removePart(idx)} className="text-red-500 hover:text-red-700">×</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={newPart.partName}
              onChange={(e) => setNewPart((p) => ({ ...p, partName: e.target.value }))}
              placeholder="Part name"
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={newPart.serialNumber}
              onChange={(e) => setNewPart((p) => ({ ...p, serialNumber: e.target.value }))}
              placeholder="Serial number"
              className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            />
          </div>
          <button onClick={addPart} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">+ Add Part</button>
        </div>

        {/* Info Box */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <AlertTriangle size={16} />
            <span>After submission, warranty claim will be reviewed by Service Head for approval</span>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${id}`)}
            className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-muted)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !claim.failureType || !claim.failureDescription || claim.sitePhotos.length === 0}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Submit Warranty Claim
          </button>
        </div>
      </div>
    </div>
  );
}